import { type Question, type SampleMaterial } from './model'

/**
 * Turns the attempts of one DEMO practice run into what the result screen and
 * the parent preview show (D-14.4): observed counts, error patterns with the
 * answers that support them, an honest evidence status and one next action.
 *
 * Rules (Exercise Framework §8, Trust Reference §3):
 * - an answer checked after opening any help is assisted: it is shown, but it
 *   is not independent evidence, so it never counts towards a pattern or a
 *   status;
 * - one wrong answer is not a pattern: a difficulty is suggested only when the
 *   same misconception tag repeats in independent answers;
 * - with too few independent answers the outcome is "insufficient evidence",
 *   whatever else was observed;
 * - a handful of answers never shows mastery, so the best outcome is "no
 *   difficulty observed".
 * These are DEMO display rules, not a mastery or weakness model.
 */

/** Below this many independent answers nothing is concluded at all. */
export const MIN_INDEPENDENT_FOR_OBSERVATION = 3

/** An error pattern is only suggested when it repeats in independent answers. */
export const MIN_REPEATS_FOR_PATTERN = 2

export interface ErrorPattern {
  readonly tag: string
  /** Independent answers showing this misconception. */
  readonly questionIds: readonly string[]
  /** Assisted answers showing it: listed for transparency, never counted. */
  readonly assistedQuestionIds: readonly string[]
  /** Independent repetitions reach MIN_REPEATS_FOR_PATTERN. */
  readonly repeated: boolean
}

export type EvidenceStatus =
  /** Too few independent answers to say anything. */
  | 'insufficient_evidence'
  /** The same misconception repeated in independent answers. */
  | 'difficulty_suggested'
  /** All independent answers correct, but a short practice cannot show mastery. */
  | 'no_difficulty_observed'
  /** Some independent answers wrong, without a repeated misconception. */
  | 'mixed'

export type NextActionKind = 'review_explanation' | 'practise_again' | 'try_new_material'

export interface NextAction {
  readonly kind: NextActionKind
  readonly text: string
  readonly reason: string
}

export interface RunSummary {
  readonly total: number
  readonly attempted: number
  readonly correct: number
  readonly incorrect: number
  /** Answers checked after opening any help before submitting. */
  readonly assisted: number
  readonly assistedCorrect: number
  readonly independent: number
  readonly independentCorrect: number
  readonly patterns: readonly ErrorPattern[]
  readonly status: EvidenceStatus
  readonly nextAction: NextAction
}

/**
 * What the evidence rules need from a run: the questions in order, and per
 * answer whether it was correct, its misconception tag and the help opened
 * before checking. DEMO runs (`Topic`, `Attempt`) and persisted runs from the
 * real school-material journey both fit.
 */
export interface QuestionList {
  readonly questions: readonly { readonly id: string }[]
}

export interface EvidenceAttempt {
  readonly questionId: string
  readonly correct: boolean
  readonly mistake: { readonly tag: string } | null
  readonly helpBeforeSubmit: readonly string[]
}

export function isAssisted(attempt: EvidenceAttempt): boolean {
  return attempt.helpBeforeSubmit.length > 0
}

/** 1-based position of a question in its topic, for "Câu 2" references. */
export function questionNumber(topic: QuestionList, questionId: string): number {
  return topic.questions.findIndex((q) => q.id === questionId) + 1
}

/** "Câu 2, 3" */
export function questionRefs(topic: QuestionList, ids: readonly string[]): string {
  return `Câu ${ids.map((id) => String(questionNumber(topic, id))).join(', ')}`
}

export function summarizeRun(
  topic: QuestionList,
  attempts: readonly EvidenceAttempt[],
): RunSummary {
  // In question order, so references read "Câu 2, 3".
  const ordered = [...attempts].sort(
    (a, b) => questionNumber(topic, a.questionId) - questionNumber(topic, b.questionId),
  )
  const correct = ordered.filter((a) => a.correct).length
  const independentAttempts = ordered.filter((a) => !isAssisted(a))
  const independent = independentAttempts.length
  const independentCorrect = independentAttempts.filter((a) => a.correct).length

  const byTag = new Map<string, { independent: string[]; assisted: string[] }>()
  for (const a of ordered) {
    if (a.mistake === null) continue
    const entry = byTag.get(a.mistake.tag) ?? { independent: [], assisted: [] }
    ;(isAssisted(a) ? entry.assisted : entry.independent).push(a.questionId)
    byTag.set(a.mistake.tag, entry)
  }
  const patterns: ErrorPattern[] = [...byTag]
    .map(([tag, ids]) => ({
      tag,
      questionIds: ids.independent,
      assistedQuestionIds: ids.assisted,
      repeated: ids.independent.length >= MIN_REPEATS_FOR_PATTERN,
    }))
    .sort((a, b) => b.questionIds.length - a.questionIds.length)

  const repeated = patterns.find((p) => p.repeated)
  const independentWrong = independent - independentCorrect

  let status: EvidenceStatus
  if (independent < MIN_INDEPENDENT_FOR_OBSERVATION) status = 'insufficient_evidence'
  else if (repeated !== undefined) status = 'difficulty_suggested'
  else if (independentWrong === 0) status = 'no_difficulty_observed'
  else status = 'mixed'

  return {
    total: topic.questions.length,
    attempted: ordered.length,
    correct,
    incorrect: ordered.length - correct,
    assisted: ordered.length - independent,
    assistedCorrect: correct - independentCorrect,
    independent,
    independentCorrect,
    patterns,
    status,
    nextAction: nextActionFor(topic, status, repeated, independentWrong, independent),
  }
}

function nextActionFor(
  topic: QuestionList,
  status: EvidenceStatus,
  repeated: ErrorPattern | undefined,
  independentWrong: number,
  independent: number,
): NextAction {
  switch (status) {
    case 'difficulty_suggested':
      return {
        kind: 'review_explanation',
        text: 'Xem lại phần giải thích, rồi luyện lại.',
        reason: `Cùng một lỗi “${repeated?.tag ?? ''}” ở ${questionRefs(topic, repeated?.questionIds ?? [])}, đều là câu tự làm.`,
      }
    case 'mixed':
      return {
        kind: 'practise_again',
        text: 'Luyện lại các câu này một lần nữa.',
        reason: `Có ${String(independentWrong)} câu tự làm chưa đúng, nhưng chưa thấy lỗi nào lặp lại.`,
      }
    case 'insufficient_evidence':
      return {
        kind: 'practise_again',
        text: 'Luyện lại, lần này thử tự làm trước khi mở trợ giúp.',
        reason: `Chỉ có ${String(independent)} câu tự làm (không mở trợ giúp), cần ít nhất ${String(MIN_INDEPENDENT_FOR_OBSERVATION)} câu để nhận xét.`,
      }
    case 'no_difficulty_observed':
      return {
        kind: 'try_new_material',
        text: 'Thử một tài liệu hoặc bài tập khác cùng chủ đề.',
        reason: 'Các câu tự làm đều đúng, nhưng vài câu chưa đủ để nói là đã nắm vững.',
      }
  }
}

/**
 * A persisted school-material run, reduced to what may count as learning
 * evidence (D-21 §1.5): only answers to independent practice. Answers to the
 * learner's own exercise (`from_source`, sets saved before concept-first) are
 * kept in the record and counted apart, never as evidence. `topic` keeps
 * every item so "Câu n" references match the numbering the learner saw.
 */
export function independentPracticeEvidence(
  items: readonly { readonly item_id: string; readonly origin: string }[],
  attempts: readonly {
    readonly item_id: string
    readonly is_correct: boolean
    readonly mistake_tag: string | null
    readonly help_used: readonly string[]
  }[],
): { topic: QuestionList; attempts: EvidenceAttempt[]; excludedSourceAnswers: number } {
  const ids = new Set(
    items.filter((i) => i.origin !== 'from_source').map((i) => i.item_id),
  )
  return {
    topic: { questions: items.map((i) => ({ id: i.item_id })) },
    attempts: attempts
      .filter((a) => ids.has(a.item_id))
      .map((a) => ({
        questionId: a.item_id,
        correct: a.is_correct,
        mistake: a.mistake_tag === null ? null : { tag: a.mistake_tag },
        helpBeforeSubmit: a.help_used,
      })),
    excludedSourceAnswers: attempts.filter((a) => !ids.has(a.item_id)).length,
  }
}

/**
 * The observed counts as one sentence, shared by the learner result and the
 * parent preview so both say the same thing. Facts only.
 */
export function observedLine(summary: RunSummary): string {
  return `Đúng ${String(summary.correct)}/${String(summary.attempted)} câu. Tự làm: đúng ${String(summary.independentCorrect)}/${String(summary.independent)}. Có trợ giúp: đúng ${String(summary.assistedCorrect)}/${String(summary.assisted)}.`
}

/** One error pattern with the questions behind it (traceability). */
export function patternLine(topic: QuestionList, pattern: ErrorPattern): string {
  const assisted =
    pattern.assistedQuestionIds.length === 0
      ? ''
      : `${questionRefs(topic, pattern.assistedQuestionIds)} có trợ giúp, không tính`
  if (pattern.questionIds.length === 0) {
    return `${pattern.tag}: chỉ gặp ở câu có trợ giúp (${assisted}).`
  }
  const independent = `${questionRefs(topic, pattern.questionIds)} tự làm`
  const verdict = pattern.repeated ? 'lặp lại' : 'mới gặp một lần, chưa đủ để kết luận'
  return `${pattern.tag}: ${independent}${assisted === '' ? '' : `; ${assisted}`} — ${verdict}.`
}

/** "Phiếu bài tập Toán: Phân số · Trang 2 · Bài 3" */
export function sourceLabel(sample: SampleMaterial): string {
  return `${sample.title} · ${sample.sourceRef}`
}

/** The extracted block a question was made from, for traceability. */
export function blockFor(sample: SampleMaterial, blockId: string) {
  return sample.extracted.find((b) => b.id === blockId) ?? null
}

/**
 * Where a question comes from: the source block, or a labelled extension
 * question on the idea of that block (never implied to be from the source).
 */
export function questionOriginLabel(sample: SampleMaterial, question: Question): string {
  const block = blockFor(sample, question.sourceBlockId)
  const label = block?.label ?? question.sourceBlockId
  return question.origin === 'extension'
    ? `Câu luyện thêm, không có trong tài liệu (cùng ý với: ${label})`
    : `Từ tài liệu: ${label}`
}
